import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface WishlistContextType {
  wishlistIds: Set<number>;
  isInWishlist: (productId: number) => boolean;
  toggleWishlist: (productId: number) => void;
  loading: boolean;
  count: number;
}

const WishlistContext = createContext<WishlistContextType | null>(null);

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
};

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [wishlistIds, setWishlistIds] = useState<Set<number>>(new Set());
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!userId) {
      setWishlistIds(new Set());
      return;
    }
    setLoading(true);
    supabase
      .from("wishlists")
      .select("product_id")
      .eq("user_id", userId)
      .then(({ data }) => {
        setWishlistIds(new Set(data?.map((r) => r.product_id) || []));
        setLoading(false);
      });
  }, [userId]);

  const isInWishlist = useCallback(
    (productId: number) => wishlistIds.has(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: number) => {
      if (!userId) {
        toast.error("Sign in to save items to your wishlist");
        return;
      }
      const inList = wishlistIds.has(productId);
      // Optimistic update
      setWishlistIds((prev) => {
        const next = new Set(prev);
        if (inList) next.delete(productId);
        else next.add(productId);
        return next;
      });

      if (inList) {
        const { error } = await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", userId)
          .eq("product_id", productId);
        if (error) {
          setWishlistIds((prev) => new Set([...prev, productId]));
          toast.error("Failed to remove from wishlist");
        } else {
          toast.success("Removed from wishlist");
        }
      } else {
        const { error } = await supabase
          .from("wishlists")
          .insert({ user_id: userId, product_id: productId });
        if (error) {
          setWishlistIds((prev) => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
          });
          toast.error("Failed to add to wishlist");
        } else {
          toast.success("Added to wishlist");
        }
      }
    },
    [userId, wishlistIds]
  );

  return (
    <WishlistContext.Provider
      value={{ wishlistIds, isInWishlist, toggleWishlist, loading, count: wishlistIds.size }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
